import { useContext, useEffect } from 'react';

import { faCalendarCirclePlus, faUsers } from '@fortawesome/pro-light-svg-icons';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';

import { RequestStatuses } from './requests';
import EmptyStateCard from 'src/components/EmptyStateCard';
import HeroCta from 'src/components/HeroCta';
import MainNav from 'src/components/MainNav';
import StyledCardContent from 'src/components/StyledCardContent';
import ThanksReceivedCallout from 'src/components/ThanksReceivedCallout';
import TopLevelPageHeader from 'src/components/TopLevelPageHeader';
import VisitCard from 'src/components/VisitCard';
import { AnalyticsContext } from 'src/contexts/AnalyticsContext';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';
import { useVisits } from 'src/hooks/useVisits';
import { InnerMaxWidthContainer } from 'src/layouts/InnerMaxWidthContainer';
import { useGetRequestsQuery } from 'src/store/sageHomeApi';

export const Route = createFileRoute('/(app)/home')({
    component: HomeComponent,
});

const PageName = 'Home Page';

function HomeComponent() {
    const { isMobile } = useResponsive();
    const { palette } = useTheme();
    const navigate = useNavigate();
    const { trackPageView } = useContext(AnalyticsContext);
    const { careRecipient, currentUser, hasOnboarded, currentTeam } = useTeamMemberData();

    const {
        isLoading,
        isUninitialized: isVisitsUninitialized,
        error: visitError,
        nextVisit,
        previousVisit,
    } = useVisits();

    const {
        data: requests = [],
        isLoading: isLoadingRequests,
        isUninitialized: isRequestsUninitialized,
        error: requestError,
    } = useGetRequestsQuery(
        {
            careRecipientRid: careRecipient?.teamMemberRid ?? '',
            lastNDays: 30,
        },
        {
            skip: !careRecipient?.teamMemberRid,
            selectFromResult: ({ data, ...rest }) => ({
                ...rest,
                data: (data ?? []).filter((request) => request.status === RequestStatuses.NEEDED),
            }),
        }
    );

    useEffect(() => {
        trackPageView(PageName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showAddTeamCta =
        !!currentUser?.isTeamAdmin && !!currentTeam && (currentTeam.members?.length ?? 0) < 4;

    const showAddVisitsCta =
        !!currentUser?.isTeamAdmin &&
        !!currentTeam &&
        (currentTeam.members?.length ?? 0) >= 2 &&
        !isLoading &&
        !isVisitsUninitialized &&
        !visitError &&
        !nextVisit &&
        !previousVisit;

    return hasOnboarded ? (
        <InnerMaxWidthContainer>
            <Stack sx={{ gap: 4 }}>
                <TopLevelPageHeader pageTitle={`Hi, ${currentUser?.firstName}!`} />

                {/* Invite Caregivers Card */}
                {showAddTeamCta && (
                    <HeroCta
                        icon={faUsers}
                        title="Stay Connected"
                        subtitle="Add members to your team to keep everyone in the loop."
                        buttonText="Invite Caregivers"
                        variant="light"
                        onClick={() => {
                            navigate({
                                to: '/care/team',
                            });
                        }}
                    />
                )}

                {showAddVisitsCta && (
                    <HeroCta
                        icon={faCalendarCirclePlus}
                        title="Peace of mind in real-time"
                        subtitle="Add visits so caregivers can log important information about what they’ve observed"
                        buttonText="Schedule a Caregiver Visit"
                        variant="dark"
                        onClick={() => {
                            navigate({
                                to: '/visits',
                            });
                        }}
                    />
                )}

                {/* Thanks Card */}
                <ThanksReceivedCallout />

                {/* Next Care Visit */}
                <Stack sx={{ gap: 1.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'purple.950' }}>
                        Next Care Visit
                    </Typography>
                    {isLoading || isVisitsUninitialized ? (
                        <Typography variant="body2">Loading...</Typography>
                    ) : visitError ? (
                        <Typography variant="body2">
                            There was an error loading visits. Please try again.
                        </Typography>
                    ) : nextVisit ? (
                        <VisitCard visit={nextVisit} showButtons showDate />
                    ) : (
                        <EmptyStateCard
                            text="Scheduled visits will appear here with date and caregiver info."
                            variant="visit"
                        />
                    )}
                </Stack>

                {/* Previous Care Visit */}
                <Stack sx={{ gap: 1.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'purple.950' }}>
                        Previous Care Visit
                    </Typography>
                    {isLoading || isVisitsUninitialized ? (
                        <Typography variant="body2">Loading...</Typography>
                    ) : visitError ? (
                        <Typography variant="body2">
                            There was an error loading visits. Please try again.
                        </Typography>
                    ) : previousVisit ? (
                        <VisitCard visit={previousVisit} showButtons showDate />
                    ) : (
                        <EmptyStateCard
                            text="Completed visits and summaries will show here."
                            variant="completed-visit"
                        />
                    )}
                </Stack>

                {/* Supply Requests */}
                <Stack sx={{ gap: 1.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'purple.950' }}>
                        Supply Requests
                    </Typography>
                    {isLoadingRequests || isRequestsUninitialized ? (
                        <Typography variant="body2">Loading...</Typography>
                    ) : requestError ? (
                        <Typography variant="body2">
                            There was an error loading supply requests. Please try again.
                        </Typography>
                    ) : requests.length > 0 ? (
                        <Card>
                            <Link to="/requests">
                                <StyledCardContent>
                                    <Stack
                                        direction="row"
                                        sx={{
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            sx={{ gap: 1.5, alignItems: 'center' }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '50%',
                                                    border: '2px solid',
                                                    borderColor: 'purple.300',
                                                    width: 48,
                                                    height: 48,
                                                }}
                                            >
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 600, color: 'purple.300' }}
                                                >
                                                    {requests.length}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="body1"
                                                fontWeight={600}
                                                color="gray.900"
                                            >
                                                Supply Requests
                                            </Typography>
                                        </Stack>
                                        <FontAwesomeIcon
                                            icon={faArrowRight}
                                            size="lg"
                                            color={palette.primary.main}
                                        />
                                    </Stack>
                                </StyledCardContent>
                            </Link>
                        </Card>
                    ) : (
                        <EmptyStateCard
                            text="Your supply requests will appear here."
                            variant="request"
                        />
                    )}
                </Stack>
            </Stack>

            {isMobile && <MainNav />}
        </InnerMaxWidthContainer>
    ) : null;
}
